import LockIcon from "@mui/icons-material/Lock";
import { Box, Card, CardActions, CardContent, CardProps, Typography } from "@mui/material";
import { GridBoxes } from "../../../component/basic/surfaces/box/grid_boxes";
import {
    Book,
    BookKind,
    BookLimit,
    BookStatus,
    BookTailorCustomKind,
} from "../../../model/book/Book";
import {
    ColorBBDisable,
    ColorBBGray,
    ColorBBGrayA3,
    ColorBBHighTitle,
    ColorBBTailorTitle,
    ColorOfficialRed,
    ColorOfficialRedA,
} from "../../../tools/color";
import complete from "./assets/side2.png";
import BookAdminBox from "./BookAdminBox";
import { GridButtonGroup } from "./GridButtonGroup";

interface BookGridsProps {
    books: Book[];
    showAdminBox?: boolean;
    onRefresh?: () => void;
    isSimulate?: boolean;
}

export function BookGrids(props: BookGridsProps) {
    const { books, showAdminBox, onRefresh, isSimulate } = props;

    const gridHeight = makeGridHeight(showAdminBox || false, isSimulate || false); // 整体格子的高度
    const cardHeight = showAdminBox ? "260" : "100%"; // 练习册的高度
    const adminBoxHeight = 140; // BookAdminBox的高度
    const contentHeight = isSimulate ? 160 : 130;

    return (
        <GridBoxes
            width={260}
            height={gridHeight}
            margin={10}
            childrens={books.map((book, index) => {
                return (
                    <>
                        <GridBase
                            sx={{
                                height: cardHeight,
                                bgcolor: makeBGColor(book),
                                boxShadow: book.template.is_tailor_custom
                                    ? "0 2px 15px rgba(244,208,63,0.5)"
                                    : undefined,
                            }}
                            cardFloat={<FloatArea book={book} />}
                            cardTitle={makeTitle(book)}
                            cardContent={makeContent(book)}
                            contentHeight={contentHeight}
                            cardButtonGroup={<GridButtonGroup book={book} />}
                        />
                        {showAdminBox && (
                            <BookAdminBox
                                temp={book.template}
                                height={adminBoxHeight}
                                onRefresh={onRefresh}
                            />
                        )}
                    </>
                );
            })}
        ></GridBoxes>
    );
}

const makeGridHeight = (showAdminBox: boolean, isSimulate: boolean) => {
    if (showAdminBox) {
        return 380;
    }
    if (isSimulate) {
        return 280;
    }
    return 260;
};

const makeBGColor = (book: Book): string => {
    const finished = book.status === BookStatus.Finish;
    if (finished) {
        return ColorOfficialRedA;
    }
    if (book.limit === BookLimit.Lock) {
        return ColorBBGrayA3;
    }
    return "white";
};

interface GridBaseProps extends CardProps {
    cardTitle: any;
    cardContent: any;
    cardFloat: any;
    cardButtonGroup: any;
    contentHeight: number;
}

const GridBase = (props: GridBaseProps) => {
    const {
        cardTitle,
        cardContent,
        cardFloat,
        cardButtonGroup,
        contentHeight,
        sx,

        ...otherProps
    } = props;

    const customSx = {
        height: "100%",
        color: "black",
        position: "relative",
        ...sx,
    } as CardProps["sx"];
    return (
        <Card sx={customSx} {...otherProps}>
            {cardFloat}
            <Box sx={{ height: 50 }} />
            <CardContent sx={{ height: contentHeight }}>
                <Typography
                    gutterBottom
                    variant="h5"
                    component="div"
                    sx={{ color: ColorBBGray, whiteSpace: "pre-wrap" }}
                >
                    {cardTitle}
                </Typography>
                <Typography variant="body2" whiteSpace="pre-line" sx={{ color: ColorBBGray }}>
                    {cardContent}
                </Typography>
            </CardContent>
            <CardActions sx={{ pt: 0 }}>{cardButtonGroup}</CardActions>
        </Card>
    );
};

function FloatArea(props: { book: Book }) {
    const { book } = props;
    return (
        <>
            {book.limit === BookLimit.Lock ? (
                <Box
                    sx={{
                        position: "absolute",
                        right: "10px",
                        top: "10px",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            ml: "10px",
                            mt: "20px",
                            mr: "20px",
                            color: ColorBBDisable,
                        }}
                    >
                        <LockIcon sx={{ fontSize: "40px" }} />
                    </Box>
                </Box>
            ) : (
                <>{book.status === BookStatus.Finish && <FinishIcon />}</>
            )}
        </>
    );
}

function FinishIcon() {
    return (
        <Box
            sx={{
                position: "absolute",
                right: "10px",
                top: "10px",
                display: "flex",
                alignItems: "center",
            }}
        >
            <Box
                sx={{
                    marginLeft: "5px",
                    color: ColorOfficialRed,
                    border: "solid 2px",
                    borderRadius: "50%",
                    padding: "5px",
                }}
            >
                <b>完成</b>
            </Box>
            <Box sx={{ marginLeft: "10px", marginTop: "5px" }}>
                <img src={complete} height="60px" width="auto" alt="完成" />
            </Box>
        </Box>
    );
}

const tailorProColor = "#FED01B";
const tailorProTextShadow = "0 0px 3px rgba(100,100,100,0.3)";

const makeTitle = (book: Book) => {
    const name = book.title;
    if (book.template.is_tailor_custom) {
        if (book.template.kind3 === BookTailorCustomKind.Pro) {
            return (
                <span
                    style={{
                        color: tailorProColor,
                        textShadow: tailorProTextShadow,
                    }}
                >
                    <b>{name}</b>
                </span>
            );
        }
        return (
            <span style={{ color: ColorBBTailorTitle }}>
                <b>{name}</b>
            </span>
        );
    }
    if (book.template.kind === BookKind.High && book.limit === BookLimit.Free) {
        return (
            <span style={{ color: ColorBBHighTitle }}>
                <b>{name}</b>
            </span>
        );
    }
    return <b>{name}</b>;
};

const makeContent = (book: Book) => {
    const name = book.sub_title;
    if (book.template.is_tailor_custom) {
        if (book.template.kind3 === BookTailorCustomKind.Pro) {
            return (
                <span
                    style={{
                        color: tailorProColor,
                        textShadow: tailorProTextShadow,
                        fontSize: 17,
                    }}
                >
                    <b>{name}</b>
                </span>
            );
        }
        return (
            <span style={{ color: ColorBBTailorTitle, fontSize: 17 }}>
                <b>{name}</b>
            </span>
        );
    }
    if (book.template.is_tailor_common) {
        return (
            <span style={{ fontSize: 17 }}>
                <b>{name}</b>
            </span>
        );
    }
    if (book.template.kind === BookKind.High && book.limit === BookLimit.Free) {
        return (
            <span style={{ color: ColorBBHighTitle }}>
                <b>{name}</b>
            </span>
        );
    }
    return (
        <b>
            {name}
            {book.expire_hint.length > 0 && (
                <>
                    <br />
                    {book.expire_hint}
                </>
            )}
        </b>
    );
};
